import type { FilterOp } from '@servicetim/shared';

const isEmpty = (v: unknown): boolean =>
  v === null || v === undefined || (Array.isArray(v) && v.length === 0) || v === '';

const asArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : isEmpty(v) ? [] : [v]);

const compare = (a: unknown, b: unknown): number | null => {
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }
  if (typeof a === 'string' && typeof b === 'string') {
    return a < b ? -1 : a > b ? 1 : 0;
  }
  return null;
};

const text = (v: unknown): string | null => (typeof v === 'string' ? v.toLowerCase() : null);

/**
 * In-memory evaluation of a filter op against a stored field value. Used by workflow
 * conditions/validators and automation conditions. `search` is not supported here
 * (it is a database operation) and always returns false.
 */
export function matchOp(op: FilterOp, actual: unknown, expected?: unknown): boolean {
  switch (op) {
    case 'eq':
      return !isEmpty(actual) && actual === expected;
    case 'neq':
      return actual !== expected;
    case 'in':
      return asArray(expected).includes(actual);
    case 'not_in':
      return !asArray(expected).includes(actual);
    case 'gt': {
      const c = compare(actual, expected);
      return c !== null && c > 0;
    }
    case 'gte': {
      const c = compare(actual, expected);
      return c !== null && c >= 0;
    }
    case 'lt': {
      const c = compare(actual, expected);
      return c !== null && c < 0;
    }
    case 'lte': {
      const c = compare(actual, expected);
      return c !== null && c <= 0;
    }
    case 'between': {
      const range = asArray(expected);
      if (range.length !== 2) {
        return false;
      }
      const lo = compare(actual, range[0]);
      const hi = compare(actual, range[1]);
      return lo !== null && hi !== null && lo >= 0 && hi <= 0;
    }
    case 'is_null':
      return isEmpty(actual);
    case 'not_null':
      return !isEmpty(actual);
    case 'contains': {
      const a = text(actual);
      const e = text(expected);
      return a !== null && e !== null && a.includes(e);
    }
    case 'starts_with': {
      const a = text(actual);
      const e = text(expected);
      return a !== null && e !== null && a.startsWith(e);
    }
    case 'any_of': {
      const have = new Set(asArray(actual));
      return asArray(expected).some((v) => have.has(v));
    }
    case 'all_of': {
      const have = new Set(asArray(actual));
      const want = asArray(expected);
      return want.length > 0 && want.every((v) => have.has(v));
    }
    case 'none_of': {
      const have = new Set(asArray(actual));
      return !asArray(expected).some((v) => have.has(v));
    }
    case 'search':
      return false;
  }
}
